export type VerifiedAccessSource = 'quota' | 'session' | 'unavailable';

export const VERIFIED_ACCESS_CHANGED_EVENT = 'xprojex:verified-access-changed';

export interface VerifiedAccessInput {
  quotaTier?: string | null;
  quotaTierName?: string | null;
  quotaHasLoaded?: boolean;
  quotaError?: string | null;
  sessionPlan?: string | null;
  sessionPlanName?: string | null;
  sessionAccessResolution?: string | null;
}

export interface VerifiedAccessProjection {
  verified: boolean;
  slug: string;
  name: string;
  source: VerifiedAccessSource;
}

const normalize = (value: string | null | undefined) => String(value || '').trim();

/**
 * Projects commercial access only from a successful quota response or a
 * backend-verified session. Missing access is intentionally not Explorer.
 */
export const resolveVerifiedAccess = (
  input: VerifiedAccessInput,
): VerifiedAccessProjection => {
  const quotaTier = normalize(input.quotaTier).toLowerCase();
  const sessionPlan = normalize(input.sessionPlan).toLowerCase();
  if (input.sessionAccessResolution === 'unavailable') {
    return {
      verified: false,
      slug: '',
      name: '',
      source: 'unavailable',
    };
  }
  const hasVerifiedSession = input.sessionAccessResolution === 'verified' && Boolean(sessionPlan);
  const hasVerifiedQuota = Boolean(
    input.quotaHasLoaded && !input.quotaError && quotaTier,
  );

  // check-session reconciles the currently signed Wix member and its active
  // order. Quota status is a stored projection and can briefly lag that
  // reconciliation, so a verified session wins only when the two disagree.
  if (hasVerifiedSession && (!hasVerifiedQuota || sessionPlan !== quotaTier)) {
    return {
      verified: true,
      slug: sessionPlan,
      name: normalize(input.sessionPlanName) || normalize(input.sessionPlan),
      source: 'session',
    };
  }

  if (hasVerifiedQuota) {
    return {
      verified: true,
      slug: quotaTier,
      name: normalize(input.quotaTierName) || normalize(input.quotaTier),
      source: 'quota',
    };
  }

  if (hasVerifiedSession) {
    return {
      verified: true,
      slug: sessionPlan,
      name: normalize(input.sessionPlanName) || normalize(input.sessionPlan),
      source: 'session',
    };
  }

  return {
    verified: false,
    slug: '',
    name: '',
    source: 'unavailable',
  };
};

export const isVerifiedExplorerAccess = (access: VerifiedAccessProjection): boolean => (
  access.verified && (access.slug === 'explorer' || access.slug === 'free')
);
